import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { toast } from 'sonner';
import { 
  Users, Shield, ShieldCheck, ShieldOff, UserCog, Key, 
  ArrowLeft, Lock, Crown, Headphones, DollarSign, Eye,
  RefreshCw, Copy, CheckCircle, AlertTriangle, Download
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const ADMIN_ROLES = [
  { 
    value: 'super_admin', 
    label: 'Super Admin', 
    icon: Crown,
    color: 'bg-red-500',
  },
  { 
    value: 'support', 
    label: 'Support', 
    icon: Headphones,
    color: 'bg-blue-500',
  },
  { 
    value: 'finance', 
    label: 'Finance', 
    icon: DollarSign,
    color: 'bg-green-500',
  },
  { 
    value: 'moderator', 
    label: 'Moderator', 
    icon: Eye,
    color: 'bg-yellow-500',
  },
];

export default function AdminManagement() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetDialog, setResetDialog] = useState({ open: false, admin: null });
  const [resetAllDialog, setResetAllDialog] = useState({ open: false, results: null });
  const [submitting, setSubmitting] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/users/admins`);
      setAdmins(response.data);
    } catch (error) {
      console.error('Error fetching admins:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. Super Admin privileges required.');
      } else {
        toast.error('Failed to load admin users');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    // Validate password complexity
    const hasUpper = /[A-Z]/.test(newPassword);
    const hasLower = /[a-z]/.test(newPassword);
    const hasDigit = /\d/.test(newPassword);

    if (!hasUpper || !hasLower || !hasDigit) {
      toast.error('Password must contain uppercase, lowercase, and numbers');
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/users/${resetDialog.admin.user_id}/reset-password`,
        { new_password: newPassword },
        
      );
      toast.success(`Password reset for ${resetDialog.admin.name}`);
      setResetDialog({ open: false, admin: null });
      setNewPassword('');
      fetchAdmins();
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetAllPasswords = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/users/reset-all-passwords`,
        {},
        
      );
      setResetAllDialog({ open: true, results: response.data.results });
      toast.success(`Passwords reset for ${response.data.results.length} admins`);
      fetchAdmins();
    } catch (error) {
      console.error('Error resetting all passwords:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset passwords');
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
      toast.success('Copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const downloadCredentials = () => {
    if (!resetAllDialog.results) return;
    
    let content = "=== NEXTGIG ADMIN CREDENTIALS ===\n";
    content += `Generated: ${format(new Date(), 'PPpp')}\n\n`;
    content += "IMPORTANT: Store these credentials securely!\n\n";
    
    resetAllDialog.results.forEach((admin, idx) => {
      content += `--- Admin ${idx + 1} ---\n`;
      content += `Name: ${admin.name}\n`;
      content += `Email: ${admin.email}\n`;
      content += `Password: ${admin.new_password}\n\n`;
    });
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `admin-credentials-${format(new Date(), 'yyyy-MM-dd')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Credentials downloaded');
  };

  const getRoleInfo = (role) => {
    return ADMIN_ROLES.find(r => r.value === role) || ADMIN_ROLES[3];
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="text-3xl font-bold">Admin Management</h1>
            <p className="text-muted-foreground">Manage admin accounts and reset passwords</p>
          </div>
        </div>

        {/* Action Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <Card className="border-red-500/30 bg-red-500/5">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-red-500/10">
                  <Key className="w-6 h-6 text-red-500" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg mb-1">Reset All Passwords</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Generate new strong passwords for all admin accounts. Credentials will be shown once.
                  </p>
                  <Button 
                    variant="destructive" 
                    onClick={handleResetAllPasswords}
                    disabled={submitting}
                    data-testid="reset-all-passwords-btn"
                  >
                    {submitting ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Key className="w-4 h-4 mr-2" />
                    )}
                    Reset All Passwords
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-primary/10">
                  <UserCog className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg mb-1">Manage Admins</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Create new admin accounts and manage roles and permissions.
                  </p>
                  <Link to="/admin/users">
                    <Button variant="outline" data-testid="manage-admins-link">
                      <Users className="w-4 h-4 mr-2" />
                      Go to Admin Users
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Admin Password Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Password Management
            </CardTitle>
            <CardDescription>
              Reset individual admin passwords. Click on an admin to set a new password.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-20 skeleton rounded-lg" />
                ))}
              </div>
            ) : admins.length > 0 ? (
              <div className="space-y-3">
                {admins.map((admin) => {
                  const roleInfo = getRoleInfo(admin.admin_role);
                  const RoleIcon = roleInfo.icon;
                  const isCurrentUser = admin.user_id === user?.user_id;
                  
                  return (
                    <div
                      key={admin.user_id}
                      className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${
                        isCurrentUser 
                          ? 'border-primary/30 bg-primary/5' 
                          : 'border-border hover:border-primary/50 hover:bg-muted/50'
                      }`}
                      data-testid={`admin-password-${admin.user_id}`}
                    >
                      {/* Avatar */}
                      <div className={`w-12 h-12 rounded-full ${roleInfo.color} flex items-center justify-center text-white font-bold text-lg`}>
                        {admin.name?.charAt(0).toUpperCase() || 'A'}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold">{admin.name}</h4>
                          {isCurrentUser && (
                            <Badge variant="outline" className="text-xs">You</Badge>
                          )}
                          <Badge className={`${roleInfo.color} text-white`}>
                            <RoleIcon className="w-3 h-3 mr-1" />
                            {roleInfo.label}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{admin.email}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          {admin.two_fa_enabled ? (
                            <span className="flex items-center gap-1 text-green-500">
                              <ShieldCheck className="w-3 h-3" /> 2FA Enabled
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-yellow-500">
                              <ShieldOff className="w-3 h-3" /> 2FA Not Setup
                            </span>
                          )}
                          {admin.password_changed_at && (
                            <span>Password changed {format(new Date(admin.password_changed_at), 'MMM d, yyyy')}</span>
                          )}
                        </div>
                      </div>

                      {/* Reset Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setResetDialog({ open: true, admin })}
                        data-testid={`reset-password-${admin.user_id}`}
                      >
                        <Key className="w-4 h-4 mr-2" />
                        Reset Password
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <UserCog className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No admin users found</h3>
                <p className="text-muted-foreground">
                  Create admin accounts from the Admin Users page.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reset Password Dialog */}
        <Dialog open={resetDialog.open} onOpenChange={(open) => !open && setResetDialog({ open: false, admin: null })}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key className="w-5 h-5" />
                Reset Password
              </DialogTitle>
              <DialogDescription>
                Set a new password for <strong>{resetDialog.admin?.name}</strong> ({resetDialog.admin?.email})
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10"
                    data-testid="new-password-input"
                  />
                </div>
              </div>

              <Alert className="bg-muted/50">
                <AlertDescription className="text-sm">
                  <p className="font-medium mb-2">Password requirements:</p>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                    <li className={newPassword.length >= 8 ? 'text-green-500' : ''}>
                      At least 8 characters
                    </li>
                    <li className={/[A-Z]/.test(newPassword) ? 'text-green-500' : ''}>
                      One uppercase letter
                    </li>
                    <li className={/[a-z]/.test(newPassword) ? 'text-green-500' : ''}>
                      One lowercase letter
                    </li>
                    <li className={/\d/.test(newPassword) ? 'text-green-500' : ''}>
                      One number
                    </li>
                  </ul>
                </AlertDescription>
              </Alert>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setResetDialog({ open: false, admin: null }); setNewPassword(''); }}>
                Cancel
              </Button>
              <Button onClick={handleResetPassword} disabled={submitting} data-testid="confirm-reset-password-btn">
                {submitting ? 'Resetting...' : 'Reset Password'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset All Results Dialog */}
        <Dialog open={resetAllDialog.open && resetAllDialog.results} onOpenChange={(open) => !open && setResetAllDialog({ open: false, results: null })}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-500">
                <CheckCircle className="w-5 h-5" />
                Passwords Reset Successfully
              </DialogTitle>
              <DialogDescription>
                New passwords have been generated for all admin accounts. Make sure to save these credentials securely.
              </DialogDescription>
            </DialogHeader>

            <Alert className="bg-yellow-500/10 border-yellow-500/50 my-4">
              <AlertTriangle className="w-4 h-4 text-yellow-500" />
              <AlertDescription className="text-yellow-200">
                <strong>Important:</strong> These passwords are shown only once. Download or copy them now.
              </AlertDescription>
            </Alert>

            <div className="space-y-3 py-4">
              {resetAllDialog.results?.map((admin) => {
                const roleInfo = getRoleInfo(admin.admin_role);
                return (
                  <div
                    key={admin.user_id}
                    className="p-4 rounded-lg border bg-muted/30"
                    data-testid={`credentials-${admin.user_id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-semibold">{admin.name}</p>
                        <p className="text-sm text-muted-foreground">{admin.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2 p-2 rounded bg-background border">
                      <code className="flex-1 text-sm font-mono">{admin.new_password}</code>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => copyToClipboard(admin.new_password, admin.user_id)}
                        data-testid={`copy-password-${admin.user_id}`}
                      >
                        {copiedId === admin.user_id ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={downloadCredentials} data-testid="download-credentials-btn">
                <Download className="w-4 h-4 mr-2" />
                Download All
              </Button>
              <Button onClick={() => setResetAllDialog({ open: false, results: null })}>
                Done
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
